import { useState, useEffect, useRef } from "react";

const LoadingScreen = ({ onComplete }: { onComplete: () => void }) => {
  const [phase, setPhase] = useState(0);
  const [done, setDone] = useState(false);
  const called = useRef(false);

  useEffect(() => {
    // Quick loading screen - just 800ms to show brand, then fade out
    const timer = setTimeout(() => {
      setDone(true);
      setTimeout(() => {
        if (!called.current) {
          called.current = true;
          onComplete();
        }
      }, 300);
    }, 800);
    return () => clearTimeout(timer);
  }, [onComplete]);

  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        background: "hsl(230 25% 5%)",
        opacity: done ? 0 : 1,
        transition: "opacity 0.4s ease-out",
        pointerEvents: done ? "none" : "all",
        contain: "layout style paint",
      }}
    >
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `linear-gradient(hsl(174 90% 50% / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(174 90% 50% / 0.3) 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
        animation: "gridMove 4s linear infinite",
        willChange: "transform",
      }} />

      {/* Floating geometric shapes - reduced to 6 */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            width: `${20 + (i % 4) * 15}px`,
            height: `${20 + (i % 4) * 15}px`,
            left: `${8 + (i * 15) % 85}%`,
            top: `${10 + (i * 13) % 75}%`,
            border: `1.5px solid ${i % 3 === 0 ? "hsl(174 90% 50% / 0.25)" : i % 3 === 1 ? "hsl(280 80% 55% / 0.2)" : "hsl(320 85% 55% / 0.15)"}`,
            borderRadius: i % 4 === 0 ? "50%" : i % 4 === 1 ? "0" : "30%",
            animation: `floatShape ${3 + (i % 3)}s ease-in-out infinite alternate`,
            animationDelay: `${i * 0.3}s`,
            transform: `rotate(${i * 60}deg)`,
            willChange: "transform, opacity",
          }}
        />
      ))}

      {/* Orbiting rings */}
      <div className="absolute w-[300px] h-[300px] sm:w-[400px] sm:h-[400px]" style={{ animation: "spinSlow 12s linear infinite", willChange: "transform" }}>
        <div className="absolute inset-0 rounded-full" style={{ border: "1px solid hsl(174 90% 50% / 0.1)" }} />
        <div className="absolute w-3 h-3 rounded-full top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ background: "hsl(174 90% 50%)", boxShadow: "0 0 15px hsl(174 90% 50% / 0.6)" }} />
      </div>
      <div className="absolute w-[220px] h-[220px] sm:w-[300px] sm:h-[300px]" style={{ animation: "spinSlow 8s linear infinite reverse", willChange: "transform" }}>
        <div className="absolute inset-0 rounded-full" style={{ border: "1px solid hsl(280 80% 55% / 0.1)" }} />
        <div className="absolute w-2.5 h-2.5 rounded-full top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ background: "hsl(280 80% 55%)", boxShadow: "0 0 12px hsl(280 80% 55% / 0.6)" }} />
      </div>

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center gap-6">
        {/* Animated logo mark */}
        <div className="relative">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, hsl(174 90% 50% / 0.15), hsl(280 80% 55% / 0.1))",
              border: "1px solid hsl(174 90% 50% / 0.25)",
              backdropFilter: "blur(10px)",
              animation: "breathe 2s ease-in-out infinite",
              willChange: "transform",
            }}
          >
            <span className="font-display font-black text-3xl" style={{ color: "hsl(174 90% 50%)", textShadow: "0 0 20px hsl(174 90% 50% / 0.4)" }}>
              M
            </span>
          </div>
          <div className="absolute -top-1 -right-1 w-3 h-3 border-t border-r" style={{ borderColor: "hsl(174 90% 50% / 0.5)" }} />
          <div className="absolute -bottom-1 -left-1 w-3 h-3 border-b border-l" style={{ borderColor: "hsl(280 80% 55% / 0.5)" }} />
        </div>

        {/* Brand name with staggered letters */}
        <div className="flex gap-[2px]">
          {"מחוברים".split("").map((letter, i) => (
            <span
              key={i}
              className="font-display font-bold text-2xl"
              style={{
                color: "hsl(174 90% 50%)",
                textShadow: "0 0 15px hsl(174 90% 50% / 0.3)",
                animation: `letterBounce 0.5s ease-out ${i * 0.07}s both`,
                opacity: phase > 0 ? 1 : 0,
              }}
            >
              {letter}
            </span>
          ))}
        </div>

        {/* CSS-animated loading bar (no JS re-renders) */}
        <div className="w-48 sm:w-64">
          <div className="h-[2px] rounded-full overflow-hidden" style={{ background: "hsl(230 20% 15%)" }}>
            <div
              className="h-full rounded-full loading-bar-fill"
              style={{
                background: "linear-gradient(90deg, hsl(174 90% 50%), hsl(280 80% 55%), hsl(320 85% 55%))",
                boxShadow: "0 0 10px hsl(174 90% 50% / 0.4)",
              }}
            />
          </div>
        </div>

        {/* Animated dots */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: "hsl(174 90% 50%)",
                animation: `dotPulse 1s ease-in-out ${i * 0.15}s infinite`,
                willChange: "transform, opacity",
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
        @keyframes floatShape {
          0% { transform: translateY(0); opacity: 0.3; }
          100% { transform: translateY(-20px); opacity: 0.7; }
        }
        @keyframes spinSlow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes letterBounce {
          0% { transform: translateY(15px); opacity: 0; }
          60% { transform: translateY(-3px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes dotPulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.8); opacity: 1; }
        }
        .loading-bar-fill {
          width: 0%;
          animation: loadProgress 0.7s ease-out forwards;
        }
        @keyframes loadProgress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
